/**
 * Notification context: unread badge count, realtime socket wiring and the
 * toast stack for incoming `notification:new` events.
 *
 * - `unreadCount` — bell badge; UNREAD_NOTIFICATION_COUNT (network-only) when
 *   a token is present, 0 for guests. A realtime `notification:read` drops it
 *   by one, except for a STAFF notice (never counted here) and a read in the
 *   Dashboard (the count is refetched instead).
 * - `inboxVersion` — bumped on every realtime `notification:new` this inbox
 *   lists (not STAFF) and on a `notification:read-all` from another mount;
 *   the inbox page listens and refetches.
 * - Socket connects when a token appears and disconnects on logout.
 * - Toast stack rendered here (fixed bottom-right): max 3, auto-dismiss after
 *   5s, click navigates via notificationRoute(), X dismisses. Built inline —
 *   deliberately NOT importing @/components/ui to avoid an import cycle.
 *
 * Must be mounted inside <BrowserRouter> (uses useNavigate) and inside
 * <ApolloProvider> and <AuthProvider>.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import type { Socket } from 'socket.io-client';
import { Bell, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { UNREAD_NOTIFICATION_COUNT } from '@/graphql/notifications';
import { connectSocket, disconnectSocket } from '@/services/socket';
import { registerWebPush, unregisterWebPush } from '@/lib/webPush';
import { notificationRoute } from '@/utils/notificationLink';
import type { RealtimeNotification, RealtimeRead, RealtimeReadAll } from '@/types';

const TOAST_DURATION_MS = 5000;
const MAX_TOASTS = 3;

interface NotificationContextType {
  /** Number of unread notifications (bell badge). 0 when logged out. */
  unreadCount: number;
  /** Refetch the unread count from the server. */
  refreshUnread: () => Promise<void>;
  /** Optimistically set the badge (used by the inbox page). */
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  /**
   * Bumped on a realtime `notification:new` this inbox lists and on another
   * mount's `notification:read-all` — the inbox listens to refetch.
   */
  inboxVersion: number;
}

const NotificationContext = createContext<NotificationContextType>({
  unreadCount: 0,
  refreshUnread: async () => {},
  setUnreadCount: () => {},
  inboxVersion: 0,
});

interface ToastItem {
  key: number;
  notification: RealtimeNotification;
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { token } = useAuth();
  const client = useApolloClient();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxVersion, setInboxVersion] = useState(0);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastKeyRef = useRef(0);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  // Set by the socket-lifecycle effect; lets the focus handler revive a
  // permanently-dead socket and migrate the event bindings to the fresh
  // instance (connectSocket rebuilds when the cached socket gave up).
  const ensureSocketRef = useRef<(() => void) | null>(null);

  const refreshUnread = useCallback(async () => {
    if (!tokenRef.current) {
      setUnreadCount(0);
      return;
    }
    try {
      const { data } = await client.query<{ unreadNotificationCount?: number }>({
        query: UNREAD_NOTIFICATION_COUNT,
        fetchPolicy: 'network-only',
      });
      if (typeof data?.unreadNotificationCount === 'number') {
        setUnreadCount(data.unreadNotificationCount);
      }
    } catch {
      // Offline / expired token — keep last known badge, never crash the UI.
    }
  }, [client]);

  const dismissToast = useCallback((key: number) => {
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const pushToast = useCallback(
    (notification: RealtimeNotification) => {
      toastKeyRef.current += 1;
      const key = toastKeyRef.current;
      setToasts((prev) => [...prev, { key, notification }].slice(-MAX_TOASTS));
      window.setTimeout(() => dismissToast(key), TOAST_DURATION_MS);
    },
    [dismissToast],
  );

  const openToast = useCallback(
    (toast: ToastItem) => {
      dismissToast(toast.key);
      const target = notificationRoute(toast.notification.link, toast.notification.data);
      navigate(target ?? '/notifications');
    },
    [dismissToast, navigate],
  );

  // Socket lifecycle, tied to the auth token.
  useEffect(() => {
    if (!token) {
      disconnectSocket();
      setUnreadCount(0);
      setToasts([]);
      return;
    }

    const onConnect = () => {
      // Catch up on anything missed while disconnected.
      void refreshUnread();
    };
    const onNew = (payload: RealtimeNotification) => {
      // The `user:<id>` room is shared with the Dashboard, so a staff user
      // gets its STAFF notices here too. This inbox never lists them: no
      // badge, no refetch, no toast. (No audience = older backend = BOTH.)
      if (payload?.audience === 'STAFF') return;
      setUnreadCount((c) => c + 1);
      setInboxVersion((v) => v + 1);
      pushToast(payload);
    };
    const onRead = (payload?: RealtimeRead) => {
      // A STAFF notice is only in the Dashboard's inbox, so this badge never
      // counted it (backend E4, docs/notifications.md §5).
      if (payload?.audience === 'STAFF') return;
      // Read in the Dashboard: that inbox also lists BOTH notices about
      // entities the public can no longer open, which this inbox hides and so
      // never counted — the server knows whether this count changed.
      if (payload?.mount === 'dashboard') {
        void refreshUnread();
        return;
      }
      // Read in the app, or an older backend (`{ id }` only): one fewer.
      setUnreadCount((c) => Math.max(0, c - 1));
    };
    const onReadAll = (payload?: RealtimeReadAll) => {
      // Only a read-all of this mount's inbox clears everything it lists. A
      // Dashboard read-all (or one without `mount`, from an older backend)
      // marked the BOTH notices read too, which this inbox also lists, but
      // left the END_USER ones unread: ask the server for the count and let
      // an open inbox page refetch its rows.
      if (payload?.mount === 'app') {
        setUnreadCount(0);
        return;
      }
      void refreshUnread();
      setInboxVersion((v) => v + 1);
    };

    const bind = (s: Socket) => {
      s.on('connect', onConnect);
      s.on('notification:new', onNew);
      s.on('notification:read', onRead);
      s.on('notification:read-all', onReadAll);
    };
    const unbind = (s: Socket) => {
      s.off('connect', onConnect);
      s.off('notification:new', onNew);
      s.off('notification:read', onRead);
      s.off('notification:read-all', onReadAll);
    };

    // connectSocket() resolves once socket.io-client has loaded (a lazy chunk),
    // to the cached socket while it is healthy, or to a fresh instance once
    // the cached one is permanently dead (reconnection attempts exhausted).
    // Our handlers move to whichever instance it gives, so the focus handler
    // goes through `adopt` too. Null (overtaken, or the chunk failed) and
    // anything resolving after this effect ended change nothing.
    let socket: Socket | null = null;
    let live = true;
    const adopt = (fresh: Socket | null) => {
      if (!live || !fresh || fresh === socket) return;
      if (socket) unbind(socket);
      socket = fresh;
      bind(fresh);
    };
    void connectSocket(token).then(adopt);
    ensureSocketRef.current = () => {
      void connectSocket(token).then(adopt);
    };

    void refreshUnread();

    return () => {
      live = false;
      ensureSocketRef.current = null;
      if (socket) unbind(socket);
    };
  }, [token, refreshUnread, pushToast]);

  // Disconnect entirely on logout/unmount.
  useEffect(() => {
    if (!token) disconnectSocket();
    return () => {
      if (!tokenRef.current) disconnectSocket();
    };
  }, [token]);

  // Browser push (FCM): register this device on login, revoke on logout. Prod-
  // only and self-deduping (see @/lib/webPush). Background OS notifications are
  // shown by the service worker; foreground in-app alerts come via the socket.
  useEffect(() => {
    if (!token) {
      void unregisterWebPush(client);
      return;
    }
    void registerWebPush(client);
  }, [token, client]);

  // Re-sync when the tab regains focus / becomes visible (the web equivalent
  // of mobile's AppState foreground handler — the socket may have dropped).
  useEffect(() => {
    const resync = () => {
      if (!tokenRef.current) return;
      // Revive realtime if the socket exhausted its reconnection attempts
      // while the tab was backgrounded (no-op when healthy), then refresh.
      ensureSocketRef.current?.();
      void refreshUnread();
    };
    const onFocus = () => resync();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resync();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshUnread]);

  return (
    <NotificationContext.Provider
      value={{ unreadCount, refreshUnread, setUnreadCount, inboxVersion }}
    >
      {children}
      {toasts.length > 0 ? (
        <div
          className="fixed bottom-4 right-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col items-stretch gap-3"
          aria-live="polite"
          aria-label="Notifications"
        >
          {toasts.map((toast) => (
            <div
              key={toast.key}
              className="flex animate-slide-in-right items-start overflow-hidden rounded-2xl border border-line bg-surface shadow-lg"
            >
              <button
                type="button"
                onClick={() => openToast(toast)}
                className="flex min-w-0 flex-1 items-start gap-3 p-4 text-left transition-all duration-200 hover:bg-background"
              >
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <Bell size={18} aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-ink">
                    {toast.notification.title || 'New notification'}
                  </span>
                  {toast.notification.body ? (
                    <span className="mt-0.5 block text-sm text-ink-secondary line-clamp-2">
                      {toast.notification.body}
                    </span>
                  ) : null}
                </span>
              </button>
              <button
                type="button"
                aria-label="Dismiss notification"
                onClick={() => dismissToast(toast.key)}
                className="m-2 shrink-0 rounded-full p-1.5 text-ink-tertiary transition-all duration-200 hover:bg-background hover:text-ink"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType =>
  useContext(NotificationContext);
